import Axios from "axios";
import { userLogout } from "store/actions";
import { store } from "store";
import { getApiBaseUrl } from "config";
import { _notifyError, printError } from "utils";

async function generateHash(payload) {
  const msgUint8 = new TextEncoder().encode(payload);
  const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// The credential is the backend's httpOnly session cookie (mvp-next ADR 0004)
// — there is no bearer token in JavaScript to attach, so `withCredentials` is
// the whole of this app's authentication.
const axios = Axios.create({
  withCredentials: true,
});

axios.interceptors.request.use(
  async (config) => {
    config.baseURL = getApiBaseUrl();

    const mutativeMethods = ["post", "put", "patch", "delete"];

    if (mutativeMethods.includes(config.method)) {
      const timeWindow = Math.floor(Date.now() / 60000); // Changes every 60 seconds
      let dataString;

      if (config.data instanceof FormData) {
        dataString = "FormData"; 
      } else {
        // Optimization: limit stringify to reasonable sizes to prevent blocking UI
        // If data is massive, we skip content hashing to preserve UI responsiveness
        // 500KB limit is conservative but safe
        try {
          dataString = JSON.stringify(config.data);
          if (dataString.length > 500000) {
            dataString = "LargePayload";
          }
        } catch (e) {
           dataString = "StringifyError";
        }
      }

      const fingerprint = `${config.method}:${config.url}:${dataString}:${timeWindow}`;
      const stableKey = await generateHash(fingerprint);
      config.headers["Indigo-Idempotency-Key"] = stableKey;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axios.interceptors.response.use(
  (res) => res,
  (err) => {
    // alert(JSON.stringify(err));
    if ((err?.response?.data?.message || "").includes("it has been deactivated")) {
      store.dispatch(userLogout());
      _notifyError(printError(err));
      throw err;
    }

    if ((err?.response?.data?.message || "").includes("not logged in")) {
      store.dispatch(userLogout());
      throw err;
    }

    throw err;
  },
);

export default axios;
